// ---------------------------------------------------------------------------
// YouAreYou (uru) — Realtime WebSocket Service
//
// Singleton service that maintains a persistent authenticated WebSocket
// connection to the backend. Follows the protocol documented in
// docs/realtime_events.md:
//
//   1. Connect to /ws/realtime/
//   2. Server sends { type: "auth_required" }
//   3. Client sends { type: "authenticate", token, prefix: "bearer" }
//   4. Server sends { type: "auth_success" }
//   5. Client is auto-subscribed to user:{id} topic
//   6. Ping every 30s to keep connection alive
//   7. On disconnect: reconnect with exponential backoff (1s → 2s → ... → 30s max)
//
// Usage:
//   import { realtime } from '@/services/realtime';
//
//   realtime.connect(token);
//   realtime.on('connection_accepted', (data) => { ... });
//   realtime.disconnect();
// ---------------------------------------------------------------------------

// Derive WS URL from the API base URL
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "https://api.youareyou.io";
const WS_URL = API_BASE
  .replace(/^https:\/\//, "wss://")
  .replace(/^http:\/\//, "ws://")
  .replace(/\/?$/, "/ws/realtime/");

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EventHandler = (data: Record<string, unknown>) => void;

// ---------------------------------------------------------------------------
// RealtimeService
// ---------------------------------------------------------------------------

class RealtimeService {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<EventHandler>>();
  private reconnectDelay = 1000;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private currentToken: string | null = null;
  /** Set to true when disconnect() is called intentionally — suppresses reconnect. */
  private intentionalClose = false;

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  connect(token: string): void {
    this.currentToken = token;
    this.intentionalClose = false;
    this._connect();
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.currentToken = null;
    this._cleanup();
    this.ws?.close(1000, "client disconnect");
    this.ws = null;
  }

  /**
   * Subscribe to an event type. Returns an unsubscribe function.
   *
   * Event types (inner data.type from topic_message):
   *   connection_accepted, connection_denied, connection_created,
   *   connection_started, kyc_completed, registration_started
   *
   * Also fires "notification" for the reserved WS notification type.
   */
  on(event: string, handler: EventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
    return () => this.off(event, handler);
  }

  off(event: string, handler: EventHandler): void {
    this.handlers.get(event)?.delete(handler);
  }

  /**
   * Subscribe to a group topic (for portal operators watching PVR events).
   * Must be called after auth_success.
   */
  subscribe(topic: string): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action: "subscribe", topic }));
    }
  }

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private _connect(): void {
    if (!this.currentToken) return;

    // Don't open a second connection if one is already open or connecting
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    try {
      this.ws = new WebSocket(WS_URL);
    } catch {
      // WebSocket construction can throw in non-browser environments (tests)
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectDelay = 1000; // Reset on successful open
    };

    this.ws.onmessage = (event: MessageEvent) => {
      let msg: Record<string, unknown>;
      try {
        msg = JSON.parse(event.data as string) as Record<string, unknown>;
      } catch {
        return;
      }

      this._handleMessage(msg);
    };

    this.ws.onerror = () => {
      // onclose will fire immediately after onerror — handle reconnect there
    };

    this.ws.onclose = () => {
      this._cleanup();
      if (!this.intentionalClose) {
        this._scheduleReconnect();
      }
    };
  }

  private _handleMessage(msg: Record<string, unknown>): void {
    const type = msg.type as string | undefined;

    switch (type) {
      case "auth_required":
        // Send authentication credentials
        this.ws?.send(
          JSON.stringify({
            type: "authenticate",
            token: this.currentToken,
            prefix: "bearer",
          }),
        );
        break;

      case "auth_success":
        this._startPing();
        break;

      case "auth_timeout":
      case "error":
        // Auth failed — don't reconnect with same token
        this.intentionalClose = true;
        this.ws?.close();
        break;

      case "pong":
        // Keepalive response — ignore
        break;

      case "topic_message": {
        const data = msg.data as Record<string, unknown> | undefined;
        if (data?.type && typeof data.type === "string") {
          this._dispatch(data.type, data);
        }
        break;
      }

      case "notification":
        // Reserved WS notification type — mirrors REST notification shape
        this._dispatch("notification", msg);
        break;

      default:
        break;
    }
  }

  private _dispatch(event: string, data: Record<string, unknown>): void {
    this.handlers.get(event)?.forEach((handler) => {
      try {
        handler(data);
      } catch (err) {
        console.warn(`[realtime] handler error for "${event}":`, err);
      }
    });
  }

  private _startPing(): void {
    this._stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: "ping" }));
      }
    }, 30_000);
  }

  private _stopPing(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private _cleanup(): void {
    this._stopPing();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private _scheduleReconnect(): void {
    if (this.intentionalClose || !this.currentToken) return;

    const delay = this.reconnectDelay;
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this._connect();
    }, delay);
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const realtime = new RealtimeService();
